/**
 * An email the command will send, from its dry run (Ben, 24 Oct): one line,
 * "The email to …", that opens to the whole email: who it goes to and who is
 * copied in, the subject, the body as plain text with its line breaks, and the
 * attachments' names. The body is text, never markup.
 */
import { ChevronRight, Mail, Paperclip } from "lucide-react";
import type { EmailBlock } from "@shared-core/domain/object-ui/contract";
import {
  addressList,
  emailHeading,
  moreLikeIt,
} from "@shared-core/domain/object-ui/email-block";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../primitives/collapsible";

function Header({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="w-16 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{value}</dd>
    </div>
  );
}

export function EmailPreview({
  block,
  defaultOpen = false,
}: {
  block: EmailBlock;
  /** Open on first draw (stories); the dialog draws it closed. */
  defaultOpen?: boolean;
}) {
  const more = moreLikeIt(block);
  return (
    <Collapsible defaultOpen={defaultOpen} className="text-sm">
      <CollapsibleTrigger className="group flex w-full min-w-0 items-center gap-2 rounded-md text-left text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Mail className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 break-words">{emailHeading(block)}</span>
        <ChevronRight
          className="size-4 shrink-0 transition-transform group-data-[state=open]:rotate-90"
          aria-hidden
        />
      </CollapsibleTrigger>
      {more && (
        <p className="mt-0.5 pl-6 text-xs text-muted-foreground">{more}</p>
      )}
      <CollapsibleContent className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2">
        <dl className="space-y-0.5">
          <Header label="To" value={addressList(block.to)} />
          {block.cc.length > 0 && (
            <Header label="Cc" value={addressList(block.cc)} />
          )}
          <Header label="Subject" value={block.subject} />
        </dl>
        <p
          data-testid="email-body"
          className="mt-2 whitespace-pre-wrap break-words border-t border-border pt-2 font-sans"
        >
          {block.body}
        </p>
        {block.attachments.length > 0 && (
          <ul className="mt-2 space-y-0.5 border-t border-border pt-2">
            {block.attachments.map((name) => (
              <li key={name} className="flex items-center gap-2">
                <Paperclip
                  className="size-3.5 shrink-0 text-muted-foreground"
                  aria-hidden
                />
                <span className="min-w-0 break-words">{name}</span>
              </li>
            ))}
          </ul>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
